import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { formatMoney, formatPlacedDate, formatPlacedTime } from '../lib/format';
import {
  humanOrderStatus,
  pickAppUserId,
  pickCustomerEmail,
  pickCustomerName,
  pickCustomerPhone,
  pickDeliveryAddress,
  pickEstimatedDeliveryText,
  pickLineItems,
  pickNestedUser,
  pickPaymentSummary,
  pickTotalAmount,
  pickFulfillmentLabel,
  pickLineItemAddons,
  pickOrderLevelAddons,
  addonLineTotal,
  orderDisplayNumber,
  statusSelectOptions,
  pickInvoiceFields,
  isCodOrder,
  isPickupOrder,
} from '../lib/orders';
import { pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner, BackLink } from '../components/PageChrome';
import { OrderTrackingTimeline } from '../components/OrderTrackingTimeline';
import { ShareOrderWhatsApp } from '../components/ShareOrderWhatsApp';
import { IconClock, IconMapPin, IconPhone, IconUser } from '../components/Icons';
import { Modal } from '../components/Modal';

function statusBadge(status) {
  const map = {
    CONFIRMED: 'info',
    PREPARING: 'warning',
    READY_FOR_PICKUP: 'primary',
    ON_THE_WAY: 'primary',
    DELIVERED: 'success',
    REFUNDED: 'warning',
    CANCELLED: 'danger',
  };
  const c = map[status] || 'secondary';
  return <span className={`badge text-bg-${c}`}>{humanOrderStatus(status)}</span>;
}

function lineItemLabel(li) {
  if (!li || typeof li !== 'object') return 'Item';
  return li.name ?? li.title ?? li.menuItemName ?? li.itemName ?? li.productName ?? 'Item';
}

function lineItemQty(li) {
  const q = li?.quantity ?? li?.qty ?? li?.count ?? 1;
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function lineItemUnitPrice(li) {
  const u = li?.unitPrice ?? li?.price ?? li?.rate;
  if (u == null) return null;
  const n = Number(u);
  return Number.isFinite(n) ? n : null;
}

function lineItemTotal(li) {
  const t = li?.lineTotal ?? li?.subtotal ?? li?.total ?? li?.amount;
  if (t != null) {
    const n = Number(t);
    if (Number.isFinite(n)) return n;
  }
  const u = lineItemUnitPrice(li);
  if (u == null) return null;
  return u * lineItemQty(li);
}

const ACTIVE_STATUSES_FOR_CANCEL = new Set([
  'PENDING',
  'CONFIRMED',
  'PREPARING',
  'READY_FOR_PICKUP',
  'ON_THE_WAY',
]);

function refundStatusBadge(status) {
  const s = String(status || '').toUpperCase();
  const map = {
    PROCESSED: 'success',
    PENDING: 'warning',
    FAILED: 'danger',
    MANUAL: 'info',
  };
  const c = map[s] || 'secondary';
  const label =
    s === 'PROCESSED'
      ? 'Refund processed'
      : s === 'PENDING'
        ? 'Refund pending (5-7 days)'
        : s === 'FAILED'
          ? 'Refund failed'
          : s === 'MANUAL'
            ? 'Manual refund recorded'
            : s;
  return <span className={`badge text-bg-${c}`}>{label}</span>;
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const reload = useCallback(async () => {
    const res = await adminApi.getOrder(orderId);
    setData(res);
    return res;
  }, [orderId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await adminApi.getOrder(orderId);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const displayNo = useMemo(() => orderDisplayNumber(data || {}), [data]);
  const lineItems = useMemo(() => pickLineItems(data || {}), [data]);
  const orderLevelAddons = useMemo(() => pickOrderLevelAddons(data || {}), [data]);
  const address = useMemo(() => pickDeliveryAddress(data || {}), [data]);
  const payment = useMemo(() => pickPaymentSummary(data || {}), [data]);
  const etaText = useMemo(() => pickEstimatedDeliveryText(data || {}), [data]);
  const fulfillment = useMemo(() => pickFulfillmentLabel(data || {}), [data]);
  const isPickup = useMemo(() => isPickupOrder(data || {}), [data]);
  const total = useMemo(() => pickTotalAmount(data || {}), [data]);
  const customerName = useMemo(() => pickCustomerName(data || {}), [data]);
  const customerEmail = useMemo(() => pickCustomerEmail(data || {}), [data]);
  const customerPhone = useMemo(() => pickCustomerPhone(data || {}), [data]);
  const appUserId = useMemo(() => pickAppUserId(data || {}), [data]);
  const nestedUser = useMemo(() => pickNestedUser(data || {}), [data]);
  const invoice = useMemo(() => pickInvoiceFields(data || {}), [data]);

  async function applyStatus(next) {
    if (!data || String(data.status).toUpperCase() === String(next).toUpperCase()) return;
    setStatusSaving(true);
    setError('');
    try {
      await adminApi.patchOrderStatus(orderId, { status: next });
      await reload();
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setStatusSaving(false);
    }
  }

  function openCancelModal() {
    setActionReason('');
    setActionError('');
    setCancelModalOpen(true);
  }

  function openRefundModal() {
    setActionReason('');
    setActionError('');
    setRefundModalOpen(true);
  }

  function closeActionModals() {
    if (actionBusy) return;
    setCancelModalOpen(false);
    setRefundModalOpen(false);
    setActionReason('');
    setActionError('');
  }

  async function submitCancel() {
    const reason = actionReason.trim();
    if (!reason) {
      setActionError('Please enter a cancellation reason.');
      return;
    }
    setActionBusy(true);
    setActionError('');
    try {
      await adminApi.cancelOrder(orderId, { reason });
      await reload();
      setCancelModalOpen(false);
      setActionReason('');
    } catch (e) {
      setActionError(pickErrorMessage(e));
    } finally {
      setActionBusy(false);
    }
  }

  async function submitRefund() {
    const reason = actionReason.trim();
    if (!reason) {
      setActionError('Please enter a refund reason.');
      return;
    }
    setActionBusy(true);
    setActionError('');
    try {
      await adminApi.refundOrder(orderId, { reason, manual: isCodOrder(data) });
      await reload();
      setRefundModalOpen(false);
      setActionReason('');
    } catch (e) {
      setActionError(pickErrorMessage(e));
    } finally {
      setActionBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (!data && !error) {
    return (
      <>
        <BackLink to="/orders">Orders</BackLink>
        <p className="text-secondary">Order not found.</p>
      </>
    );
  }

  const curStatus = String(data?.status || '').toUpperCase();
  const placedAt = data?.placedAt ?? data?.createdAt ?? data?.orderedAt ?? data?.submittedAt;
  const notes = data?.notes ?? data?.customerNotes ?? data?.specialInstructions ?? data?.instructions ?? '';

  return (
    <>
      <BackLink to="/orders">Orders</BackLink>
      <PageHeader
        title={`Order ${displayNo}`}
        subtitle={
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            {statusBadge(data?.status)}
            {fulfillment ? (
              <span
                className={`badge rounded-pill ${
                  isPickup ? 'text-bg-warning text-dark fw-semibold' : 'text-bg-light border'
                }`}
              >
                {isPickup ? '🏬 Self Pickup' : '🛵 Delivery'}
              </span>
            ) : null}
            {placedAt ? (
              <span className="text-secondary small d-inline-block">
                <span className="d-block text-dark">{formatPlacedDate(placedAt)}</span>
                <time className="d-block order-placed-time" dateTime={placedAt}>
                  {formatPlacedTime(placedAt)}
                </time>
              </span>
            ) : null}
          </span>
        }
        actions={
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <label className="small text-secondary mb-0 me-1">Status</label>
            <select
              className="form-select form-select-sm w-auto"
              value={curStatus}
              disabled={statusSaving || curStatus === 'REFUNDED' || curStatus === 'CANCELLED'}
              aria-busy={statusSaving}
              onChange={(e) => applyStatus(e.target.value)}
            >
              {statusSelectOptions(data?.status, data).map((s) => (
                <option key={s} value={s}>
                  {humanOrderStatus(s)}
                </option>
              ))}
            </select>
            {ACTIVE_STATUSES_FOR_CANCEL.has(curStatus) ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={openCancelModal}
                disabled={statusSaving}
              >
                Cancel order
              </button>
            ) : null}
            {curStatus === 'DELIVERED' && !isCodOrder(data) ? (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={openRefundModal}
                disabled={statusSaving}
              >
                Process refund
              </button>
            ) : null}
          </div>
        }
      />
      <ErrorBanner message={error} />

      <div className="row g-3 g-lg-4">
        <div className="col-lg-7 col-xl-8">
          <Card className="mb-3">
            <CardBody className="p-3 p-md-4">
              <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Fulfillment tracking</h2>
              <p className="small text-secondary mb-4">
                Same lifecycle your customer sees in the app — timestamps appear when your API records status history
                or per-stage timestamps.
              </p>
              <OrderTrackingTimeline order={data} />
            </CardBody>
          </Card>

          <Card className="mb-3 overflow-hidden border-0 shadow-sm">
            <CardBody className="p-0">
              <div className="order-detail-items-head px-3 px-md-4 py-3">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-0">Items & add-ons</h2>
              </div>
              {lineItems.length === 0 && orderLevelAddons.length === 0 ? (
                <div className="p-4 text-secondary small border-top">No line items on this payload. Check raw details below if the API nests them differently.</div>
              ) : (
                <div className="admin-table-shell order-detail-items-shell mb-0">
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0 admin-data-table order-detail-items-table">
                      <thead>
                        <tr>
                          <th scope="col">Item</th>
                          <th scope="col" className="text-end">
                            Qty
                          </th>
                          <th scope="col" className="text-end">
                            Each
                          </th>
                          <th scope="col" className="text-end">
                            Line
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li, idx) => {
                          const rowKey = li.lineItemId ?? li.id ?? li.menuItemId ?? idx;
                          const pq = lineItemQty(li);
                          const addons = pickLineItemAddons(li);
                          return (
                            <React.Fragment key={rowKey}>
                              <tr>
                                <td>
                                  <div className="fw-semibold text-dark">{lineItemLabel(li)}</div>
                                  {li.variantName || li.sizeName || li.optionsLabel ? (
                                    <div className="small text-secondary mt-1">{li.variantName ?? li.sizeName ?? li.optionsLabel}</div>
                                  ) : null}
                                </td>
                                <td className="text-end tabular-nums text-secondary">{pq}</td>
                                <td className="text-end tabular-nums text-secondary">{formatMoney(lineItemUnitPrice(li))}</td>
                                <td className="text-end tabular-nums fw-semibold text-dark">{formatMoney(lineItemTotal(li))}</td>
                              </tr>
                              {addons.map((ad, ai) => (
                                <tr key={`${rowKey}-addon-${ai}`} className="order-line-addon-row">
                                  <td className="ps-md-4">
                                    <span className="order-line-addon-prefix text-secondary" aria-hidden>
                                      +
                                    </span>
                                    <span className="small text-secondary">{ad.name}</span>
                                    {ad.quantity > 1 ? (
                                      <span className="small text-secondary"> ×{ad.quantity}</span>
                                    ) : null}
                                  </td>
                                  <td className="text-end small text-secondary">—</td>
                                  <td className="text-end tabular-nums small text-secondary">
                                    {ad.price != null ? formatMoney(ad.price) : '—'}
                                  </td>
                                  <td className="text-end tabular-nums small text-secondary">{formatMoney(addonLineTotal(ad, pq))}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                        {orderLevelAddons.map((ad, ai) => (
                          <tr
                            key={`order-addon-${ai}`}
                            className={`order-line-addon-row order-order-addon-row${ai === 0 ? ' order-detail-order-addons-start' : ''}`}
                          >
                            <td className="ps-md-4">
                              <span className="order-line-addon-prefix text-secondary" aria-hidden>
                                +
                              </span>
                              <span className="small fw-medium text-dark">{ad.name}</span>
                              <span className="badge rounded-pill text-bg-light border ms-2 small fw-normal">Order</span>
                            </td>
                            <td className="text-end small text-secondary">{ad.quantity > 1 ? ad.quantity : '—'}</td>
                            <td className="text-end tabular-nums small text-secondary">
                              {ad.price != null ? formatMoney(ad.price) : '—'}
                            </td>
                            <td className="text-end tabular-nums small fw-medium text-dark">{formatMoney(addonLineTotal(ad, 1))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="col-lg-5 col-xl-4">
          {etaText ? (
            <Card className="mb-3 order-detail-eta shadow-sm">
              <CardBody className="d-flex gap-3 align-items-center p-3 p-md-4">
                <div className="order-detail-eta__icon" aria-hidden>
                  <IconClock size={20} strokeWidth={2} />
                </div>
                <div>
                  <div className="small text-secondary text-uppercase fw-semibold mb-1">
                    {isPickup ? 'Estimated pickup' : 'Estimated delivery'}
                  </div>
                  <div className="h5 mb-0 text-dark fw-bold">{etaText}</div>
                </div>
              </CardBody>
            </Card>
          ) : null}

          <Card className="mb-3">
            <CardBody className="p-3 p-md-4">
              <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Totals</h2>
              <dl className="row small mb-0">
                <dt className="col-6 text-secondary">Subtotal</dt>
                <dd className="col-6 text-end tabular-nums">{formatMoney(data?.subtotal ?? data?.itemsSubtotal)}</dd>
                <dt className="col-6 text-secondary">Tax</dt>
                <dd className="col-6 text-end tabular-nums">{formatMoney(data?.taxAmount ?? data?.tax)}</dd>
                <dt className="col-6 text-secondary">Delivery</dt>
                <dd className="col-6 text-end tabular-nums">
                  {isPickup ? (
                    <span className="text-success fw-medium">Free (Self Pickup)</span>
                  ) : (
                    formatMoney(data?.deliveryFee ?? data?.deliveryCharge)
                  )}
                </dd>
                <dt className="col-6 text-secondary">Discount</dt>
                <dd className="col-6 text-end tabular-nums text-success">
                  {data?.discountAmount != null || data?.discount != null ? `−${formatMoney(data?.discountAmount ?? data?.discount)}` : '—'}
                </dd>
                <dt className="col-6 fw-semibold text-dark pt-2">Total</dt>
                <dd className="col-6 text-end tabular-nums fw-bold text-dark pt-2">{formatMoney(total)}</dd>
              </dl>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody className="p-3 p-md-4">
              <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Customer</h2>
              <div className="fw-semibold text-dark">{customerName || '—'}</div>
              {customerEmail ? (
                <div className="small text-break mt-1">
                  <a href={`mailto:${customerEmail}`}>{customerEmail}</a>
                </div>
              ) : null}
              {customerPhone ? (
                <div className="small mt-1">
                  <a href={`tel:${String(customerPhone).replace(/\s/g, '')}`}>{customerPhone}</a>
                </div>
              ) : null}
              {appUserId ? (
                <div className="mt-3">
                  <Link to={`/app-users/${appUserId}`} className="btn btn-sm btn-outline-primary">
                    App profile
                  </Link>
                </div>
              ) : nestedUser && typeof nestedUser === 'object' ? (
                <p className="small text-secondary mb-0 mt-2">Linked user object present; no stable id for profile link.</p>
              ) : null}
            </CardBody>
          </Card>

          {isPickup ? (
            <Card className="mb-3 border-warning border-opacity-50">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex gap-3">
                  <div
                    className="order-detail-eta__icon bg-warning bg-opacity-10 text-dark"
                    style={{ width: '2.5rem', height: '2.5rem' }}
                    aria-hidden
                  >
                    <IconMapPin size={18} />
                  </div>
                  <div className="min-w-0 flex-grow-1">
                    <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-2">Fulfillment</h2>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="badge text-bg-warning text-dark fw-bold">🏬 STORE PICKUP</span>
                    </div>
                    <div className="fw-semibold text-dark mb-1">Takeaway / Counter Pickup</div>
                    <p className="small text-secondary mb-2">
                      Customer will collect order directly from restaurant counter. No delivery agent dispatched.
                    </p>
                    {customerPhone ? (
                      <div className="d-flex align-items-center gap-2 small text-secondary mt-1">
                        <IconPhone size={14} className="text-secondary flex-shrink-0" />
                        <a href={`tel:${String(customerPhone).replace(/\s/g, '')}`} className="text-reset text-decoration-none">
                          {customerPhone}
                        </a>
                      </div>
                    ) : null}
                  </div>
                </div>
              </CardBody>
            </Card>
          ) : address ? (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex gap-3">
                  <div className="order-detail-eta__icon" style={{ width: '2.5rem', height: '2.5rem' }} aria-hidden>
                    <IconMapPin size={18} />
                  </div>
                  <div className="min-w-0 flex-grow-1">
                    <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-2">Delivery address</h2>
                    {address.name || address.contactNumber ? (
                      <div className="border-bottom pb-2 mb-2">
                        {address.name ? (
                          <div className="d-flex align-items-center gap-2 fw-semibold text-dark">
                            <IconUser size={15} className="text-secondary flex-shrink-0" />
                            <span className="text-break">{address.name}</span>
                          </div>
                        ) : null}
                        {address.contactNumber ? (
                          <div className="d-flex align-items-center gap-2 small text-secondary mt-1">
                            <IconPhone size={14} className="text-secondary flex-shrink-0" />
                            <a
                              href={`tel:${String(address.contactNumber).replace(/\s/g, '')}`}
                              className="text-reset text-decoration-none"
                            >
                              {address.contactNumber}
                            </a>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {address.label ? <div className="fw-semibold text-dark mb-1">{address.label}</div> : null}
                    {address.lines.map((ln, i) => (
                      <div key={i} className="small text-secondary text-break">
                        {ln}
                      </div>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          ) : null}

          <ShareOrderWhatsApp order={data} />

          {(invoice.invoiceNumber || invoice.invoiceUrl) && (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Invoice</h2>
                {invoice.invoiceNumber ? (
                  <div className="fw-semibold text-dark mb-2">{invoice.invoiceNumber}</div>
                ) : null}
                {invoice.invoiceUrl ? (
                  <a
                    href={invoice.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    Download PDF
                  </a>
                ) : (
                  <p className="small text-secondary mb-0">Invoice PDF is not available yet.</p>
                )}
              </CardBody>
            </Card>
          )}

          {(payment.method || payment.status || payment.id) && (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Payment</h2>
                <dl className="row small mb-0">
                  {payment.method ? (
                    <>
                      <dt className="col-5 text-secondary">Method</dt>
                      <dd className="col-7 text-end text-break">{payment.method}</dd>
                    </>
                  ) : null}
                  {payment.status ? (
                    <>
                      <dt className="col-5 text-secondary">Status</dt>
                      <dd className="col-7 text-end">{payment.status}</dd>
                    </>
                  ) : null}
                  {payment.id ? (
                    <>
                      <dt className="col-5 text-secondary">Ref</dt>
                      <dd className="col-7 text-end text-break small font-monospace">{payment.id}</dd>
                    </>
                  ) : null}
                </dl>
              </CardBody>
            </Card>
          )}

          {data?.refund ? (
            <Card className="mb-3 border-warning-subtle">
              <CardBody className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-0">Refund</h2>
                  {refundStatusBadge(data.refund.status)}
                </div>
                <dl className="row small mb-0">
                  <dt className="col-5 text-secondary">Amount</dt>
                  <dd className="col-7 text-end tabular-nums fw-semibold text-dark">
                    {formatMoney(data.refund.amount)}
                  </dd>
                  <dt className="col-5 text-secondary">Initiated</dt>
                  <dd className="col-7 text-end">
                    <span className="d-block">{formatPlacedDate(data.refund.initiatedAt)}</span>
                    <time className="d-block order-placed-time" dateTime={String(data.refund.initiatedAt)}>
                      {formatPlacedTime(data.refund.initiatedAt)}
                    </time>
                  </dd>
                  {data.refund.processedAt ? (
                    <>
                      <dt className="col-5 text-secondary">Processed</dt>
                      <dd className="col-7 text-end">
                        <span className="d-block">{formatPlacedDate(data.refund.processedAt)}</span>
                        <time className="d-block order-placed-time" dateTime={String(data.refund.processedAt)}>
                          {formatPlacedTime(data.refund.processedAt)}
                        </time>
                      </dd>
                    </>
                  ) : null}
                  {data.refund.reason ? (
                    <>
                      <dt className="col-5 text-secondary">Reason</dt>
                      <dd className="col-7 text-end text-break">{data.refund.reason}</dd>
                    </>
                  ) : null}
                </dl>
              </CardBody>
            </Card>
          ) : null}

          {data?.cancellation ? (
            <Card className="mb-3 border-danger-subtle">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-3">Cancellation</h2>
                <dl className="row small mb-0">
                  <dt className="col-5 text-secondary">Cancelled at</dt>
                  <dd className="col-7 text-end">
                    <span className="d-block">{formatPlacedDate(data.cancellation.at)}</span>
                    <time className="d-block order-placed-time" dateTime={String(data.cancellation.at)}>
                      {formatPlacedTime(data.cancellation.at)}
                    </time>
                  </dd>
                  {data.cancellation.reason ? (
                    <>
                      <dt className="col-5 text-secondary">Reason</dt>
                      <dd className="col-7 text-end text-break">{data.cancellation.reason}</dd>
                    </>
                  ) : null}
                </dl>
              </CardBody>
            </Card>
          ) : null}

          {notes ? (
            <Card className="mb-3">
              <CardBody className="p-3 p-md-4">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-2">Notes</h2>
                <p className="small mb-0 text-break">{String(notes)}</p>
              </CardBody>
            </Card>
          ) : null}

        </div>
      </div>

      {cancelModalOpen ? (
        <Modal
          title="Cancel order"
          onClose={closeActionModals}
          footer={
            <div className="d-flex justify-content-end gap-2 w-100">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeActionModals}
                disabled={actionBusy}
              >
                Keep order
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={submitCancel}
                disabled={actionBusy || !actionReason.trim()}
              >
                {actionBusy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Cancelling…
                  </>
                ) : (
                  'Cancel & refund'
                )}
              </button>
            </div>
          }
        >
          <p className="small text-secondary">
            This will mark order <strong>{displayNo}</strong> as cancelled.
            {isCodOrder(data)
              ? ' COD orders are cancelled without a payment gateway call.'
              : ' A full Razorpay refund will be issued automatically — the customer will be notified.'}
          </p>
          <label className="form-label small text-secondary mb-1" htmlFor="cancel-reason">
            Reason (shared with customer)
          </label>
          <textarea
            id="cancel-reason"
            className="form-control"
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            maxLength={500}
            disabled={actionBusy}
            placeholder="e.g. Out of stock for one of the items"
          />
          {actionError ? (
            <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert">
              {actionError}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {refundModalOpen ? (
        <Modal
          title="Process refund"
          onClose={closeActionModals}
          footer={
            <div className="d-flex justify-content-end gap-2 w-100">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeActionModals}
                disabled={actionBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={submitRefund}
                disabled={actionBusy || !actionReason.trim()}
              >
                {actionBusy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Refunding…
                  </>
                ) : (
                  `Refund ${formatMoney(total)}`
                )}
              </button>
            </div>
          }
        >
          <p className="small text-secondary mb-2">
            This will mark order <strong>{displayNo}</strong> as refunded
            {isCodOrder(data)
              ? ' and record a manual refund entry for audit (no Razorpay call — cash already collected).'
              : ' and issue a full Razorpay refund.'}
          </p>
          <p className="small text-secondary mb-3">
            Customer will be notified via push.
            {!isCodOrder(data)
              ? ' Refund typically reflects in their bank within 5-7 business days (instant for UPI / RuPay where supported).'
              : ''}
          </p>
          <label className="form-label small text-secondary mb-1" htmlFor="refund-reason">
            Reason (shared with customer)
          </label>
          <textarea
            id="refund-reason"
            className="form-control"
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            maxLength={500}
            disabled={actionBusy}
            placeholder="e.g. Customer reported missing item"
          />
          {actionError ? (
            <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert">
              {actionError}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
